import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Text from '@adsk/bim360-matrix-react-text';

import Label from '@adsk/bim360-matrix-react-label';
import './_Field.scss';

import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';

/**
 * A component that aggregates both a label and another input type.
 */
const Field = props => {
  const { children: child, valueGetter } = props;
  const childProps = child ? child.props : {};

  const childValue = child && valueGetter ? valueGetter(child) : childProps.value;
  const childPlaceholder = childProps.placeholder;

  const containerClasses = classNames('Field', `Field--${props.position}`, props.className);
  const readOnlyTextClasses = classNames('Field__readonly-value', props.readOnlyClassName, {
    'Field__readonly-value--empty': !childValue
  });

  return (
    <div className={containerClasses}>
      <Label className="Field__label">{props.label}</Label>
      {props.readOnly ? (
        <Text className={readOnlyTextClasses} ellipsis={true}>
          {childValue || childPlaceholder}
        </Text>
      ) : (
        child
      )}
    </div>
  );
};

Field.LABEL_POSITION = {
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal'
};

Field.defaultProps = {
  position: Field.LABEL_POSITION.VERTICAL,
  readOnly: false
};

Field.propTypes = {
  /**
   * An additional class to assign on the component's container
   */
  className: PropTypes.string,
  /**
   * An additional class to assign on the text component when the value is read only.
   */
  readOnlyClassName: PropTypes.string,
  /**
   * The component's children
   */
  children: PropTypes.node,
  /**
   * Label prefix before the dropdown
   */
  label: PropTypes.string,
  /**
   * Position of the label in relation to the child
   */
  position: PropTypes.oneOf(objectValues(Field.LABEL_POSITION)),
  /**
   * Whether to display selection as text only, so UI visualization indicating editing is possible
   */
  readOnly: PropTypes.bool,
  /**
   * A custom value getter function if the component doesn't have a value property
   */
  valueGetter: PropTypes.func
};

export default Field;
